import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { CircularProgress, Switch, TextField } from "@mui/material";
import { KeyRound, ShieldCheck } from "lucide-react";
import {
    getMasterUserProfileDetailAction,
    updateMasterUserProfileAction,
} from "../../Actions/ProfileActions";
import CustomSnackbar from "../Common/snackBar";
import {
    API_IMAGE_URL,
    CRYPTO_TOKEN_SECRET_KEY,
} from "../../config";
import CryptoJS from "crypto-js";
import { useUser } from "../../components/ContextAPI/ProfileContext";
import { getUserData } from "../../Actions/common";
import { enableTwoFactorAuth } from "../../Actions/AuthActions";
import ChangePasswordComponent from "./ChangePassword_index";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import ProfilePictureUpload from "../../components/Common/ProfilePictureUpload";
import showConfirmation from "../../utils/Conformation";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords } from "../../utils/badWord";
import "./ChangeProfile.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const getAuthToken = () => {
    const stored = localStorage.getItem("token");
    if (!stored) return null;
    return CryptoJS.AES.decrypt(stored, CRYPTO_TOKEN_SECRET_KEY).toString(
        CryptoJS.enc.Utf8
    );
};

const getInitials = (firstName = "", lastName = "") => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
};

const validationSchema = yup.object().shape({
    firstName: yup
        .string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("First name"))
        .min(1, VALIDATION.MIN_LENGTH("First name", 1))
        .max(50, VALIDATION.MAX_LENGTH("First name", 50))
        .test(
            "bad-word",
            "First Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .matches(
            /^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/,
            VALIDATION.INVALID_FORMAT("First name")
        ),
    lastName: yup
        .string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("Last name"))
        .min(1, VALIDATION.MIN_LENGTH("Last name", 1))
        .max(50, VALIDATION.MAX_LENGTH("Last name", 50))
        .test(
            "bad-word",
            "Last Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .matches(
            /^[a-zA-Z_\-'\u2018\u2019\u2018\u0060\u2013\u2014]+$/,
            VALIDATION.INVALID_FORMAT("Last name")
        ),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(["jpeg", "png", "jpg"]),
            (value) => {
                if (!value) return true;
                if (!(value instanceof File)) return true;
                return ["image/jpeg", "image/png", "image/jpg"].includes(
                    value.type
                );
            }
        )
        .test(
            "fileSize",
            VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"),
            (value) => {
                if (!value || !(value instanceof File)) return true;
                return value.size <= 2 * 1024 * 1024;
            }
        ),
});

const ChangeMasterUserProfile = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { updateUserData } = useUser();
    const authToken = getAuthToken();

    const [openModal, setOpenModal] = useState(location.state?.openModel || false);
    const [userData, setUserData] = useState(null);
    const [isImageEdited, setIsImageEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const getUserProfileDetails = async () => {
        setLoading(true);
        try {
            const res = await getMasterUserProfileDetailAction();
            setUserData(res?.data?.data);
            updateUserData(res?.data?.data);

            if (res?.data) {
                let stored = localStorage.getItem("userData")
                    ? getUserData()
                    : null;
                stored = {
                    ...stored,
                    firstName: res.data.data.firstName,
                    lastName: res.data.data.lastName,
                    profilePic: res.data.data.profilePic,
                };
                localStorage.setItem(
                    "userData",
                    CryptoJS.AES.encrypt(
                        JSON.stringify(stored),
                        CRYPTO_TOKEN_SECRET_KEY
                    ).toString()
                );
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message || "Failed to load profile",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authToken) {
            setSnackbar({
                open: true,
                message: "Invalid token! Please login again",
                severity: "error",
            });
            const timer = setTimeout(() => navigate("/login"), 1500);
            return () => clearTimeout(timer);
        }
        getUserProfileDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: userData?.firstName || "",
            lastName: userData?.lastName || "",
            profilePic: userData?.profilePic || null,
            profilePicPreview: userData?.profilePic
                ? `${API_IMAGE_URL}/qbotmasterapp.netcluesdemo.com/profilePics/${userData.profilePic}`
                : "",
            twoFactor: userData?.twoFactor || false,
        },
        validationSchema,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setSpinner(true);

            const formData = new FormData();
            formData.append("firstName", values.firstName);
            formData.append("lastName", values.lastName);
            formData.append("isImageEdited", isImageEdited);
            formData.append("twoFactor", values.twoFactor);
            if (values.profilePic) {
                formData.append("profilePic", values.profilePic);
            }

            try {
                const res = await updateMasterUserProfileAction(formData);
                if (res.status === "202") {
                    setSnackbar({
                        open: true,
                        message: res.data.message,
                        severity: "info",
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: res.data.message,
                        severity: "success",
                    });
                    getUserProfileDetails();
                }
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.message ?? error.error,
                    severity: "error",
                });
            } finally {
                setSpinner(false);
                setButtonDisable(false);
            }
        },
    });

    const handleTwoFactorToggle = async () => {
        const isEnabling = !formik.values.twoFactor;

        await showConfirmation("toggleStatus", {
            entityName: "Two-Factor Authentication",
            entityId: null,
            currentStatus: formik.values.twoFactor,
            action: async () => {
                const data = {
                    twoFactor: isEnabling ? "Enable" : "Disable",
                };
                return await enableTwoFactorAuth(data);
            },
            onSuccess: (res, newStatus) => {
                setSnackbar({
                    open: true,
                    message:
                        res?.data?.message ||
                        `Two-Factor Authentication ${
                            newStatus ? "enabled" : "disabled"
                        } successfully.`,
                    severity: res?.data?.status === 200 ? "success" : "info",
                });
                getUserProfileDetails();
            },
            onError: (error) => {
                setSnackbar({
                    open: true,
                    message: error?.message || "Something went wrong",
                    severity: "error",
                });
            },
        });
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        navigate(location.pathname, {
            replace: true,
            state: { ...location.state, openModel: false },
        });
    };

    const handleNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ""));
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleFileSelect = (file) => {
        if (userData) setIsImageEdited(true);
        formik.setFieldValue("profilePic", file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                formik.setFieldValue("profilePicPreview", e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePicture = () => {
        setIsImageEdited(true);
        formik.setFieldValue("profilePic", null);
        formik.setFieldValue("profilePicPreview", "");
    };

    const displayName =
        [formik.values.firstName, formik.values.lastName]
            .filter(Boolean)
            .join(" ") || "Your profile";

    return (
        <div id="kt_content_container" className="pf-page">
            {loading && (
                <div className="pf-loading" aria-busy="true" aria-live="polite">
                    <CircularProgress size={36} sx={{ color: "#22419e" }} />
                </div>
            )}

            <BreadcrumbsComponent />

            <div className="pf-identity">
                <span className="pf-identity-avatar" aria-hidden>
                    {formik.values.profilePicPreview ? (
                        <img src={formik.values.profilePicPreview} alt="" />
                    ) : (
                        getInitials(
                            formik.values.firstName,
                            formik.values.lastName
                        )
                    )}
                </span>
                <div className="pf-identity-copy">
                    <h1>{displayName}</h1>
                    <p>{userData?.email || "Update your account details"}</p>
                </div>
                <div className="pf-identity-meta">
                    <span
                        className={`pf-chip${
                            formik.values.twoFactor ? " is-on" : ""
                        }`}
                    >
                        {formik.values.twoFactor
                            ? "2FA enabled"
                            : "2FA disabled"}
                    </span>
                </div>
            </div>

            <form className="pf-panel" onSubmit={formik.handleSubmit}>
                <div className="pf-panel-head">
                    <div>
                        <h2>Profile details</h2>
                        <p>
                            Your name and photo appear across the Master Portal.
                        </p>
                    </div>
                </div>

                <div className="pf-panel-body">
                    <div className="pf-details">
                        <div className="pf-photo">
                            <ProfilePictureUpload
                                onFileSelect={handleFileSelect}
                                onRemove={handleRemovePicture}
                                accept="image/jpeg,image/png,image/jpg"
                                maxSize={2 * 1024 * 1024}
                                error={
                                    formik.touched.profilePic &&
                                    formik.errors.profilePic
                                        ? formik.errors.profilePic
                                        : null
                                }
                                preview={formik.values.profilePicPreview}
                                firstName={
                                    formik.values.firstName ||
                                    userData?.firstName ||
                                    ""
                                }
                                lastName={
                                    formik.values.lastName ||
                                    userData?.lastName ||
                                    ""
                                }
                                size={120}
                            />
                            <p className="pf-photo-hint">
                                JPG or PNG, up to 2&nbsp;MB.
                            </p>
                        </div>

                        <div className="pf-fields">
                            <div className="pf-field">
                                <label htmlFor="firstName">
                                    First name
                                    <span className="pf-req">*</span>
                                </label>
                                <TextField
                                    id="firstName"
                                    placeholder="Enter first name"
                                    name="firstName"
                                    size="small"
                                    type="text"
                                    value={formik.values.firstName}
                                    onChange={handleNameChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.firstName &&
                                        Boolean(formik.errors.firstName)
                                    }
                                    helperText={
                                        formik.touched.firstName &&
                                        formik.errors.firstName
                                    }
                                    fullWidth
                                />
                            </div>

                            <div className="pf-field">
                                <label htmlFor="lastName">
                                    Last name
                                    <span className="pf-req">*</span>
                                </label>
                                <TextField
                                    id="lastName"
                                    placeholder="Enter last name"
                                    name="lastName"
                                    size="small"
                                    type="text"
                                    value={formik.values.lastName}
                                    onChange={handleNameChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.lastName &&
                                        Boolean(formik.errors.lastName)
                                    }
                                    helperText={
                                        formik.touched.lastName &&
                                        formik.errors.lastName
                                    }
                                    fullWidth
                                />
                            </div>

                            <div className="pf-field is-full">
                                <label htmlFor="email">Email</label>
                                <TextField
                                    id="email"
                                    size="small"
                                    name="email"
                                    value={userData?.email || ""}
                                    fullWidth
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pf-panel-footer">
                    <button
                        type="submit"
                        className="pf-btn is-primary"
                        disabled={buttonDisable || spinner || !formik.isValid}
                    >
                        Save changes
                        {spinner && (
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                            />
                        )}
                    </button>
                </div>
            </form>

            <div className="pf-panel">
                <div className="pf-panel-head">
                    <div>
                        <h2>Sign-in &amp; security</h2>
                        <p>
                            Manage how you sign in and protect this account.
                        </p>
                    </div>
                </div>

                <div className="pf-panel-body">
                    <div className="pf-security-list">
                        <div className="pf-security-row">
                            <span className="pf-security-icon" aria-hidden>
                                <KeyRound size={20} strokeWidth={2} />
                            </span>
                            <div className="pf-security-copy">
                                <h3>Password</h3>
                                <p>
                                    Use a strong password with letters, numbers,
                                    and special characters.
                                </p>
                            </div>
                            <div className="pf-security-actions">
                                <button
                                    type="button"
                                    className="pf-btn"
                                    onClick={() => setOpenModal(true)}
                                >
                                    Change password
                                </button>
                            </div>
                        </div>

                        <div className="pf-security-row">
                            <span
                                className={`pf-security-icon${
                                    formik.values.twoFactor ? " is-on" : ""
                                }`}
                                aria-hidden
                            >
                                <ShieldCheck size={20} strokeWidth={2} />
                            </span>
                            <div className="pf-security-copy">
                                <h3>Two-factor authentication</h3>
                                <p>
                                    Require a 6-digit code in addition to your
                                    password when you sign in.
                                </p>
                            </div>
                            <div className="pf-security-actions">
                                <span
                                    className={`pf-chip${
                                        formik.values.twoFactor ? " is-on" : ""
                                    }`}
                                >
                                    {formik.values.twoFactor
                                        ? "On"
                                        : "Off"}
                                </span>
                                <Switch
                                    checked={Boolean(formik.values.twoFactor)}
                                    onChange={handleTwoFactorToggle}
                                    inputProps={{
                                        "aria-label":
                                            "Toggle two-factor authentication",
                                    }}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: "#22419e",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                            {
                                                backgroundColor: "#22419e",
                                            },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ChangePasswordComponent
                open={openModal}
                onClose={handleCloseModal}
                snackbar={snackbar}
                setSnackbar={setSnackbar}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </div>
    );
};

export default ChangeMasterUserProfile;
